import { Group } from "@mantine/core";
import type { MRT_ColumnDef } from "mantine-react-table-open";
import {
  MantineReactTable,
  useMantineReactTable
} from "mantine-react-table-open";
import { useMemo } from "react";
import { usePersons } from "../../../hooks/usePersons";
import { useVariantGenerators } from "../../../hooks/useVariantGenerators";
import { getDefaultMRTOptions } from "../../../mantine/getDefaultMRTOptions";
import { getMRTTopToolbarCustomActionsRenderer } from "../../../mantine/getMRTTopToolbarCustomActionsRenderer";
import { useToast } from "../../../mantine/notifications/useToast";
import { useResetPageOnFilterOrSortChange } from "../../../mantine/useResetPageOnFilterOrSortChange";
import type { GeneratorOverview, Person } from "../../../utils/backend-types";
import { emptyArray } from "../../../utils/constants";
import { buildNestedObject } from "../../BuildingBlocks/Forms/utils/buildNestedObject";
import type { GeneratorFeatureKeys } from "../../ComponentListContainer/ComponentList/ComponentEditWizard/Data/utils/getGeneratorFeatures";
import { GENERATOR_FEATURES } from "../../ComponentListContainer/ComponentList/ComponentEditWizard/Data/utils/getGeneratorFeatures";
import { getMaStRColumn } from "../../Direktvermarktung/DirektvermarktungContractInformation/DirektvermarktungGeneratorShareTable/Columns/getMaStRColumn";
import { LoadOrError } from "../../LoadOrError/LoadOrError";
import { useShouldShowStaffView } from "../../StaffViewToggle/useShouldShowStaffView";
import { GeneratorsPageTableBatchActions } from "./GeneratorsPageTableBatchActions";
import { getGeneratorFeatureColumn } from "./getGeneratorFeatureColumn";
import { useGeneratorFeatureMutation } from "./useGeneratorFeatureMutation";

interface GeneratorsPageProps {
  variantId: number;
}

function GeneratorsPage({ variantId }: GeneratorsPageProps) {
  const {
    data: generators,
    isLoading: isLoadingGenerators,
    error: generatorsError
  } = useVariantGenerators(variantId);
  const {
    persons,
    isLoading: isLoadingPersons,
    error: personsError
  } = usePersons({ variantId });
  return (
    <LoadOrError
      error={generatorsError || personsError}
      loading={isLoadingGenerators || isLoadingPersons}
    >
      {generators && persons && (
        <GeneratorsList
          generators={generators}
          persons={persons}
          variantId={variantId}
        />
      )}
    </LoadOrError>
  );
}

interface GeneratorsListProps {
  generators: Array<GeneratorOverview>;
  variantId: number;
  persons: Array<Person>;
}

function BuildEmsysAvailableValue(generator: GeneratorOverview): string {
  if (!generator.is_redispatch_import_active) {
    return "Kein Redispatch-Import aktiv";
  }
  if (!generator.is_vollmacht_provided) {
    return "Bitte Vollmacht bereitstellen";
  }
  if (!generator.date_of_first_emsys_data) {
    return "Warten auf EMSYS Spiegelung";
  }
  return `Verfügbar ab ${generator.date_of_first_emsys_data}`;
}

function GeneratorsList({
  generators,
  variantId,
  persons
}: GeneratorsListProps) {
  const { showErrorToast } = useToast();
  const { shouldShowStaffView: isStaff } = useShouldShowStaffView();
  const { editGeneratorFeature } = useGeneratorFeatureMutation(variantId);
  const { showToast } = useToast();
  const isPending = editGeneratorFeature.isPending;

  const handleChangeStatusBatchVerificationItems = async (
    key: GeneratorFeatureKeys,
    value: boolean
  ) => {
    const feature = GENERATOR_FEATURES.find((item) => item.key === key);

    const selectedRows = table
      .getSelectedRowModel()
      .flatRows.map((row) => row.original)
      .filter(
        (row) => !feature?.generatorCondition || row[feature.generatorCondition]
      );

    const results = await Promise.allSettled(
      selectedRows.map((row) =>
        editGeneratorFeature.mutateAsync(
          {
            id: row.id,
            ...buildNestedObject(key, value)
          },
          {
            onError: () => {
              showErrorToast(
                `Die Änderungen am Erzeuger ${row.name} konnten nicht gespeichert werden.`
              );
            }
          }
        )
      )
    );

    table.resetRowSelection();
    const successCount = results.filter((r) => r.status === "fulfilled").length;
    showToast({
      type: "success",
      message: `${successCount} Erzeuger aktualisiert.`
    });
  };

  const cols = useMemo<Array<MRT_ColumnDef<GeneratorOverview>>>(
    () => [
      {
        header: "Name",
        accessorKey: "name"
      },
      getMaStRColumn({
        siteIdKey: "site",
        generatorIdKey: "id"
      }),
      {
        header: "TR-ID",
        accessorKey: "tr_id"
      },
      {
        header: "Liegenschaft",
        accessorKey: "site_name",
        filterVariant: "multi-select"
      },
      {
        id: "person",
        header: "Unternehmen",
        accessorFn: (row) =>
          persons?.find((person) => person.id === row.person)?.name || "",
        filterVariant: "multi-select"
      },
      ...GENERATOR_FEATURES.filter((feature) =>
        generators?.some(
          (generator) =>
            !feature.generatorCondition || generator[feature.generatorCondition]
        )
      )
        .filter((feature) => isStaff || !feature.staffOnly)
        .map((feature) => getGeneratorFeatureColumn({ feature, variantId })),
      ...(generators?.some(
        (generator) => generator.is_redispatch_import_active_for_operator
      )
        ? [
            {
              id: "date_of_first_emsys_data",
              header: "Verfügbarkeit der EMSYS Daten",
              accessorFn: (row) => BuildEmsysAvailableValue(row)
            }
          ]
        : [])
    ],
    [variantId, persons, generators, isStaff]
  );

  const resetPageState = useResetPageOnFilterOrSortChange();

  const table = useMantineReactTable<GeneratorOverview>({
    ...getDefaultMRTOptions({
      hideBorder: true,
      initialState: {
        showGlobalFilter: true
      }
    }),
    ...getMRTTopToolbarCustomActionsRenderer(),
    autoResetPageIndex: false,
    onGlobalFilterChange: resetPageState.onGlobalFilterChange,
    onColumnFiltersChange: resetPageState.onColumnFiltersChange,
    onSortingChange: resetPageState.onSortingChange,
    onPaginationChange: resetPageState.onPaginationChange,
    state: resetPageState.state,
    enableColumnFilters: true,
    enableFacetedValues: true,
    enableFilterMatchHighlighting: false,
    columnFilterDisplayMode: "popover",
    enableGlobalFilter: true,
    globalFilterFn: "includesString",
    renderTopToolbarCustomActions: () => {
      return (
        <Group justify="flex-end" p={0} w="stretch">
          <GeneratorsPageTableBatchActions
            batchIsDisabled={isPending || !hasTableSelection}
            handleChangeStatusBatchVerificationItems={
              handleChangeStatusBatchVerificationItems
            }
          />
        </Group>
      );
    },
    columns: cols,
    data: generators || emptyArray
  });

  const hasTableSelection = table.getSelectedRowModel().flatRows.length > 0;

  return <MantineReactTable table={table} />;
}

export { GeneratorsPage };
